'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createApplication,
  deleteApplication,
  getApplicationSubmissions,
  getApplications,
  reviewApplicationSubmission,
  seedApplication,
  sendApplication,
  type ApplicationInput
} from '@/lib/api/provider/applications';
import { queryKeys } from '@/lib/query';

export const useApplications = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.provider.applications.list(),
    queryFn: getApplications,
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true
  });

export const useApplicationSubmissions = (
  filters?: {
    applicationId?: number;
    conversationId?: number;
    reviewStatus?: string;
  },
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.provider.applications.submissions(filters),
    queryFn: () => getApplicationSubmissions(filters),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true
  });

const useInvalidateApplications = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.provider.applications.all()
    });
};

export const useSeedApplication = () => {
  const invalidate = useInvalidateApplications();
  return useMutation({
    mutationFn: (preset: 'starter' | 'blank') => seedApplication(preset),
    onSuccess: invalidate
  });
};

export const useCreateApplication = () => {
  const invalidate = useInvalidateApplications();
  return useMutation({
    mutationFn: (input: ApplicationInput) => createApplication(input),
    onSuccess: invalidate
  });
};

export const useDeleteApplication = () => {
  const invalidate = useInvalidateApplications();
  return useMutation({
    mutationFn: (id: number) => deleteApplication(id),
    onSuccess: invalidate
  });
};

export const useSendApplication = () => {
  const invalidate = useInvalidateApplications();
  return useMutation({
    mutationFn: ({
      id,
      conversationId,
      message
    }: {
      id: number;
      conversationId: number;
      message?: string;
    }) => sendApplication(id, { conversationId, message }),
    onSuccess: invalidate
  });
};

export const useReviewSubmission = () => {
  const invalidate = useInvalidateApplications();
  return useMutation({
    mutationFn: ({
      id,
      reviewStatus,
      reviewNote
    }: {
      id: number;
      reviewStatus: 'pending' | 'done';
      reviewNote?: string | null;
    }) => reviewApplicationSubmission(id, { reviewStatus, reviewNote }),
    onSuccess: invalidate
  });
};
